import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useAPIInterceptor } from './utils/apiInterceptor';  
import PageLayout from './components/layout/PageLayout';
import LoginButton from './components/LoginButton';
import './styles/unified.css';
import Toast from './components/common/Toast';
import { ActingAsProvider } from './context/ActingAsContext';
import ActingAsBanner from './components/common/ActingAsBanner';

// Error handling components - NEW
import ErrorBoundary from './components/ErrorBoundary';
import Support from './components/pages/Support';
import Explore from './components/pages/Explore';

// Page imports
import Dashboard from './components/pages/Dashboard';
import SlotTemplate from './components/pages/Templates';
import Bookings from './components/pages/Bookings';
import ManageBookings from './components/pages/ManageBookings';
import Students from './components/pages/Students';
import Instructors from './components/pages/Instructors';
import Payments from './components/pages/Payments';
import Report from './components/pages/Report';
import Notifications from './components/pages/Notifications';
import Others from './components/pages/Others';
import StudentSignupPage from './components/pages/StudentSignupPasswordless';
import InstructorSignupPage from './components/pages/InstructorSignupPage';
import Homepage from './components/pages/Homepage';
import HomepageMinimal from './components/pages/Homepage-Minimal';
import ManageTracker from './components/pages/ManageTracker';
import AdminManageTracker from './components/pages/admin/AdminManageTracker';
import ManageAdmins from './components/pages/admin/ManageAdmins';
import MyProgress from './components/pages/MyProgress';
import MyPayments from './components/pages/MyPayments';
import MyAccount from './components/pages/MyAccount';
import PrivacyPolicy from './components/pages/static/PrivacyPolicy';
import Terms from './components/pages/static/Terms';
import StudentLoginPage from './components/pages/StudentLoginPage';
import StudentLayout from './components/layout/StudentLayout';
import { useStudentAuth } from './context/StudentAuthContext';

function App() {
  const { isLoading, isAuthenticated, error } = useAuth0();
  const { isStudentAuthenticated } = useStudentAuth();

  useAPIInterceptor(); 

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading authentication...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Authentication Error</h2>
        <p>{error.message}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  // Cognito student session (OTP login) — checked before the public branch
  // so a signed-in student doesn't fall through to the "please log in"
  // screen. Deliberately its own route tree/layout rather than sharing the
  // Auth0-protected one below: StudentLayout has no Auth0 dependency, and
  // this tree only ever contains the four student-relevant routes.
  if (!isAuthenticated && isStudentAuthenticated) {
    return (
      <Router>
        <ErrorBoundary>
          <StudentLayout>
            <Routes>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/bookings" element={<Bookings />} />
              <Route path="/my-progress" element={<MyProgress />} />
              <Route path="/my-payments" element={<MyPayments />} />
              <Route path="/my-account" element={<MyAccount />} />
              {/* Anything else (including "/") lands on the student's home */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </StudentLayout>
        </ErrorBoundary>
        <Toast />
      </Router>
    );
  }

  // Public routes (no authentication required, no sidebar)
  if (!isAuthenticated) {
    return (
      <Router>
        {/* Wrap with ErrorBoundary to catch errors in public routes */}
        <ErrorBoundary>
          <Routes>
            <Route path="/" element={<Homepage />} />
            <Route path="/minimal" element={<HomepageMinimal />} />
            {/* Support/Error page - NEW - Must be accessible without auth */}
            <Route path="/support" element={<Support />} />
            <Route path="/explore" element={<Explore />} />
            
            {/* Public signup route - accessible without login */}
            <Route path="/signup" element={<StudentSignupPage />} />
            
            {/* Public instructor signup route - accessible without login */}
            <Route path="/instructorsignup" element={<InstructorSignupPage />} />
            
            {/* Static pages - publicly accessible without login */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />

            <Route path="/student-login" element={<StudentLoginPage />} />

            {/* Default login page for all other routes */}
            <Route path="*" element={
              <div className="login-container">
                <div className="login-card">
                  <h1>Driving Instructor Booking System</h1>
                  <p>Please log in to continue</p>
                  <LoginButton />
                </div>
              </div>
            } />
          </Routes>
        </ErrorBoundary>
      </Router>
    );
  }

  // Protected routes (authentication required, with sidebar)
  return (
    <ActingAsProvider>
    <Router>
      {/* Wrap with ErrorBoundary to catch errors in protected routes */}
      <ErrorBoundary>
        <Routes>
          {/* Static pages — no sidebar, no PageLayout */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/support" element={<Support />} />

          {/* All other protected routes — wrapped in PageLayout (with sidebar) */}
          <Route path="*" element={
            <PageLayout>
              <ActingAsBanner />
              <Routes>
                <Route path="/signup" element={<StudentSignupPage />} />
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/template" element={<SlotTemplate />} />
                <Route path="/bookings" element={<Bookings />} />
                <Route path="/manage-students" element={<Students />} />
                <Route path="/instructors" element={<Instructors />} />
                <Route path="/manage-admins" element={<ManageAdmins />} />
                <Route path="/payments" element={<Payments />} />
                <Route path="/report" element={<Report />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/manage-bookings" element={<ManageBookings />} />
                <Route path="/others" element={<Others />} />
                <Route path="/manage-tracker" element={<ManageTracker />} />
                <Route path="/admin-tracker" element={<AdminManageTracker />} />
                <Route path="/my-progress" element={<MyProgress />} />
                <Route path="/my-payments" element={<MyPayments />} />
                <Route path="/my-account" element={<MyAccount />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </PageLayout>
          } />
        </Routes>
      </ErrorBoundary>
    <Toast />
    </Router>
    </ActingAsProvider>
  );
}

export default App;